/**
 * AtlasTravelModule — SWG travel surfaces on the ground:
 *
 *   · SHUTTLEPORT (cities + towns): stand at the pad → K opens the World Atlas
 *     in TRAVEL mode; pick any city or town → `shuttleTravel` hops you there
 *     for a 10 SOV fare (server-validated boarding + fee).
 *   · SPACEPORT (cities only): stand at the landed terminal ship → Y launches
 *     (`enterSpace`) into the sector; the client then ADOPTS the space world
 *     the server moved us into (atlas:world-adopted — no enterWorld re-call).
 *
 * Port spots are deterministic (lib/ports) — the same math the settlement
 * renderer used to place the structures, well inside the server's 260 m
 * settlement-center checks.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { spaceportSpot, shuttleSpot } from '../lib/ports.js';
// space campaign E (2026-09-30): the port run is taken on TERMS (the window), with the interact key — H is the manual.
import { makeSystem, Phase } from '../engine-compat.js';
import { interactFocus } from '../lib/interact_focus.js';
import { keyIs, keyHint } from '../lib/keybinds.js';
import { onInteract } from '../lib/interact_input.js';
import { portWindow } from '../lib/port_window.js';
import { ensurePortRows, statusOf } from '../lib/port_client.js';
import { dryLines } from '../lib/port_terms.js';

const PROMPT_RANGE_M = 34;

interface MapModuleLike { openTravel(): void }

export class AtlasTravelModule implements IModule {
  readonly name = 'AtlasTravel';
  private app: App | null = null;
  private prompt: HTMLDivElement | null = null;
  private timer: number | null = null;
  private sites: SettlementSite[] = [];
  private seed = '';
  private nearShuttle = false;
  private nearSpaceport = false;
  /** The planetary port (2026-09-17): the run the interact key acts on at this spaceport, resolved each tick
   *  (space campaign E: E through the ONE KEY, ONE FOCUS arbiter — H always opened the manual over it). */
  private portAction: { kind: 'take' | 'land'; haulId: bigint } | null = null;
  private portDist = 0;
  private portSubbed = false;

  init(app: App): void {
    this.app = app;
    this.prompt = document.createElement('div');
    this.prompt.style.cssText =
      'position:fixed;bottom:160px;left:50%;transform:translateX(-50%);z-index:34;display:none;' +
      'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:6px 14px;' +
      'font:12px var(--ax-font);color:var(--ax-text);box-shadow:0 4px 14px rgba(0,0,0,.5);text-align:center';
    document.body.appendChild(this.prompt);

    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.spaceFlight) return;
      if (e.code === 'KeyK' && this.nearShuttle) {
        (this.app?.getModule('AtlasWorldMap') as unknown as MapModuleLike | null)?.openTravel();
      } else if (e.code === 'KeyY' && this.nearSpaceport) {
        void this.launch();
      } else if (e.code === 'KeyL' && this.nearSpaceport) {
        // THE GROUND BRIDGE (Space Wave 2): market goods board the active hull here.
        const conn = atlas.conn;
        if (conn) {
          (conn.reducers as unknown as { loadHoldFromGround(a: Record<string, never>): Promise<void> })
            .loadHoldFromGround({})
            .then(() => bus.emit('atlas:status', { text: 'Hold loaded — your market goods are aboard' }))
            .catch((err: Error) => bus.emit('atlas:status', { text: `Load refused: ${err.message ?? err}` }));
        }
      }
    });
    // The port run answered a KEYDOWN only: a pad or touch player saw the prompt and could not take or land a run.
    // onInteract hears the bound key AND the pad (one press), and refuses a focused text field itself.
    onInteract(() => {
      if (atlas.loadingGate || atlas.spaceFlight) return;
      if (this.nearSpaceport && this.portAction && interactFocus().owns('port')) {
        // The planetary port: an UP run is taken on its TERMS (the window names owner, custodian, loss rule, split …);
        // a DOWN run in your hold is landed here — the crates stay the port's until then.
        const act = this.portAction;
        if (act.kind === 'take') portWindow().open({ kind: 'terms', haulId: act.haulId });
        else {
          const conn = atlas.conn;
          const R = conn?.reducers as unknown as { portDeliver(a: { haulId: bigint }): Promise<void> } | undefined;
          R?.portDeliver({ haulId: act.haulId })
            .then(() => { bus.emit('atlas:toast', { text: 'The run is landed — the escrow is paid by its terms; the town buys the ore as it needs', kind: 'success' } as never); portWindow().open({ kind: 'receipt', haulId: act.haulId }); })
            .catch((err: Error) => bus.emit('atlas:toast', { text: `Port refused: ${err.message ?? err}`, kind: 'error' } as never));
        }
      }
    });
    this.timer = window.setInterval(() => this.tick(), 600);
    // THE CLAIM IS PER FRAME: the 600 ms scan finds the run, every frame re-claims E for it (priority 3, a terminal's).
    app.world.addSystem(makeSystem('AtlasPortClaim', Phase.UI, 38, () => {
      if (this.nearSpaceport && this.portAction && !atlas.spaceFlight) interactFocus().claim({ id: 'port', dist: this.portDist, priority: 3, label: this.portAction.kind === 'take' ? 'port run terms' : 'land the port run' });
    }));
  }

  private ensureSites(): void {
    const conn = atlas.conn;
    if (!conn) return;
    if (!this.portSubbed) {
      // The port rows the ground prompt reads (the sector subscribes them too; a second handle is harmless) — and the
      // slice's terms / receipts / dry-chain status (space campaign E).
      this.portSubbed = true;
      try { conn.subscriptionBuilder().subscribe(['SELECT * FROM planetary_port', "SELECT * FROM port_haul WHERE state = 'offered'", "SELECT * FROM port_haul WHERE state = 'aboard'"]); } catch { /* pre-publish server */ }
      ensurePortRows();
    }
    for (const w of conn.db.world.iter()) {
      if (w.kind !== 'commons') continue;
      if (this.seed !== w.terrainSeed) { this.seed = w.terrainSeed; this.sites = settlementSites(this.seed); }
      return;
    }
  }

  /** The port line(s) for a city's spaceport prompt, and the run H acts on. */
  private portLines(siteId: number): string[] {
    this.portAction = null;
    const conn = atlas.conn;
    if (!conn) return [];
    interface PortRow { portId: bigint; settlementId: bigint; state: string }
    interface HaulRow { haulId: bigint; portId: bigint; direction: string; qty: number; rewardSov: bigint; state: string; hauler: { isEqual(o: unknown): boolean }; stationKey: string }
    const db = conn.db as unknown as { planetaryPort?: { iter(): Iterable<PortRow> }; portHaul?: { iter(): Iterable<HaulRow> } };
    let port: PortRow | null = null;
    try { for (const p of db.planetaryPort?.iter() ?? []) if (p.settlementId === BigInt(siteId + 1)) { port = p; break; } } catch { return []; }
    if (!port) return [];
    const me = conn.identity;
    let runs: HaulRow[] = [];
    try { runs = [...(db.portHaul?.iter() ?? [])].filter((h) => h.portId === port!.portId); } catch { runs = []; }
    const K = keyHint('interact');
    const landing = me ? runs.find((h) => h.state === 'aboard' && h.direction === 'down' && h.hauler.isEqual(me)) : undefined;
    if (landing) { this.portAction = { kind: 'land', haulId: landing.haulId }; return [`<b>${K}</b> — Land the port run here (${landing.qty} u ore · ${landing.rewardSov} ⓒ)`]; }
    const offer = runs.find((h) => h.state === 'offered' && h.direction === 'up');
    if (offer) { this.portAction = { kind: 'take', haulId: offer.haulId }; return [`<b>${K}</b> — Port run to ${offer.stationKey}: read the terms (${offer.qty} u ore · ${offer.rewardSov} ⓒ escrowed)`]; }
    const aboard = runs.find((h) => h.state === 'aboard');
    if (aboard) return [`Port: a run is ${aboard.hauler.isEqual(me) ? 'in your hold — land it docked at ' + aboard.stationKey : 'aboard another pilot\'s hull'}`];
    // THE DRY CHAIN (space campaign E): why nothing is offered, in the port's own numbers (its last hourly step).
    const st = statusOf(port.portId);
    if (!st) return [`Port: ${port.state} · no run on offer`];
    let reasons: string[] = [], numbers: Record<string, string | number> = {};
    try { reasons = JSON.parse(st.reasonsJson) as string[]; numbers = JSON.parse(st.numbersJson) as Record<string, string | number>; } catch { /* */ }
    const why = dryLines(st.procure, reasons.filter((r) => r !== 'pending'), numbers).slice(0, 2);
    return [`Port: no run on offer — ${why.length ? why.join('; ') : port.state}`.replace(/[<>&]/g, '')];
  }

  private tick(): void {
    if (!this.prompt) return;
    this.nearShuttle = false;
    this.nearSpaceport = false;
    if (atlas.spaceFlight || atlas.loadingGate || atlas.currentWorldId === null
      || atlas.currentWorldId !== atlas.commonsWorldId) {
      this.prompt.style.display = 'none';
      return;
    }
    this.ensureSites();
    const lines: string[] = [];
    for (const s of this.sites) {
      const dC = Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ);
      if (dC > 400) continue;
      const shut = shuttleSpot(this.seed, s);
      if (shut && Math.hypot(s.centerX + shut.x - atlas.localX, s.centerZ + shut.z - atlas.localZ) < PROMPT_RANGE_M) {
        this.nearShuttle = true;
        lines.push('<b>K</b> — Board shuttle · instant travel (10 ⓒ fare)');
      }
      const sport = spaceportSpot(this.seed, s);
      if (sport && Math.hypot(s.centerX + sport.x - atlas.localX, s.centerZ + sport.z - atlas.localZ) < PROMPT_RANGE_M + 24) {
        this.nearSpaceport = true;
        lines.push('<b>Y</b> — Launch to the sector (spaceport)');
        lines.push('<b>L</b> — Load the ship\'s hold with your market goods');
        lines.push(...this.portLines(s.id));
        this.portDist = Math.hypot(s.centerX + sport.x - atlas.localX, s.centerZ + sport.z - atlas.localZ);
      }
      if (lines.length) break;
    }
    if (lines.length && !atlas.spaceFlight) { // a ground prompt has no business in the cockpit (ladder v2)
      this.prompt.innerHTML = lines.join('<br>');
      this.prompt.style.display = 'block';
    } else this.prompt.style.display = 'none';
  }

  private async launch(): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    try {
      await conn.reducers.enterSpace({});
    } catch (e) {
      bus.emit('atlas:status', { text: `Launch refused: ${(e as Error).message ?? e}` });
      return;
    }
    // The server moved our playerState into the space world — adopt it.
    let spaceId: bigint | null = null;
    for (let i = 0; i < 40 && spaceId === null; i++) {
      for (const w of conn.db.world.iter()) if (w.kind === 'space') { spaceId = w.worldId; break; }
      if (spaceId === null) await new Promise((r) => setTimeout(r, 250));
    }
    if (spaceId === null) { bus.emit('atlas:status', { text: 'The sector did not answer — try again' }); return; }
    const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    bus.emit('atlas:world-adopted', { worldId: spaceId, x: me?.posX ?? 0, z: me?.posZ ?? 0 });
  }

  dispose(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.prompt?.remove();
  }
}
